import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios';
import { DELETE, GET, PATCH, POST, PUT } from './constants';

/**
 * @typedef RequestOptions
 * @type {Object}
 * @property {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
 * @property {Object=} data - an object that will be converted into the querystring
 * for GET or converted to JSON for any other method
 * @property {Array.<string>} route - array of strings to append as path segments
 */
/**
 * Service class for making API calls, wrapping a base class with token-aware authorization headers — extends `DataServiceBase` and injects Bearer tokens from MSAL via `setTokenGetter`.
 */
class DataService {
  tokenGetterMethod: (options?: any) => Promise<string> = () => new Promise<string>((r) => r(''));
  axiosInstance: AxiosInstance;
  locale = null;

  async setTokenGetter(tokenGetter: (options: any) => Promise<string>) {
    this.tokenGetterMethod = tokenGetter;
  }

  constructor(baseURL: string, errorHandler = console.error, tokenGetter = undefined) {
    this.axiosInstance = axios.create({ baseURL });
    if (tokenGetter) this.setTokenGetter(tokenGetter);
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const idToken = await this.tokenGetterMethod();
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${idToken}`
          // 'Access-Control-Allow-Credentials': 'true',
        } as any;
        return config;
      },
      (error) => {
        errorHandler(error, this.locale);
        return Promise.reject(error.response);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Non 2xx status codes
        errorHandler(error, this.locale);
        // const originalRequest = error.config;
        // if (error.response.status === 401 && !originalRequest._retry) {
        //   originalRequest._retry = true;
        //   const {idToken} = await this.tokenGetterMethod.acquireTokenSilent({forceRefresh: true});
        //   axios.defaults.headers.common.Authorization = `Bearer ${idToken}`;
        //   return this.axiosInstance(originalRequest);
        // }
        return Promise.reject(error.response);
      }
    );
  }

  /**
   * Initiates an api request
   *
   * @param { RequestOptions } options
   */
  request({
    method = GET,
    data,
    route = [''],
    params,
    formData,
    responseType,
    onDownloadProgress,
    onUploadProgress
  }: any) {
    const config: AxiosRequestConfig = {
      method,
      url: ''
    } as any;

    if (params) {
      config.params = params;
    }

    if (data) config.data = data;

    if (route && route.length > 0) {
      config.url += route.join('/');
    }
    if (responseType) {
      config.responseType = responseType;
    }
    if (onDownloadProgress) {
      config.onDownloadProgress = onDownloadProgress;
    }
    if (onUploadProgress) {
      config.onUploadProgress = onUploadProgress;
    }
    if (formData) {
      // config.formData = formData;
      config.headers = { 'Content-Type': 'multipart/form-data' } as any;
      if (config.method === POST) {
        return this.axiosInstance.post(config.url!, formData, config);
      }
      if (config.method === PUT) {
        return this.axiosInstance.put(config.url!, formData, config);
      }
    }

    return this.axiosInstance.request(config);
  }

  /**
   * @example:
   *  meh.get({route:['someRoute']});
   * @param route array of routes
   * @param params
   * @returns {Promise<AxiosResponse<any>>}
   */
  /**
   * Performs a GET request — delegates to the base DataService class with the `GET` HTTP method.
   *
   * @param options - Options including `route` (path segments), `data`, and `params`.
   * @returns A Promise resolving to the API response.
   */
  get({ route = [], params }: any) {
    return this.request({ method: GET, route, params } as any);
  }
  /**
   * Performs a GET request for a binary blob — same as `get` but requests `responseType: 'blob'`.
   *
   * @param options - Options including `route`, `params`, and optional `onDownloadProgress` callback.
   * @returns A Promise resolving to a Blob.
   */
  getBlob({
    route = [],
    params,
    onDownloadProgress
  }: {
    route: Array<string>;
    params?: any;
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }) {
    return this.request({ method: GET, route, params, responseType: 'blob', onDownloadProgress } as any);
  }

  /**
   * Performs a POST request — delegates to the base DataService class with the `POST` HTTP method.
   *
   * @param options - Options including `route`, `data`, `params`, `formData`, and optional `onUploadProgress` callback.
   * @returns A Promise resolving to the API response.
   */
  post({ route = [], data, params, formData, onUploadProgress }: any) {
    return this.request({
      method: POST,
      route,
      data,
      params,
      formData,
      onUploadProgress
    });
  }

  /**
   * Performs a PATCH request — delegates to the base DataService class with the `PATCH` HTTP method.
   *
   * @param options - Options including `route`, `data`, and `params`.
   * @returns A Promise resolving to the API response.
   */
  patch({ route = [], data, params }: any) {
    return this.request({
      method: PATCH,
      route,
      data,
      params
    });
  }

  /**
   * Performs a PUT request — delegates to the base DataService class with the `PUT` HTTP method.
   *
   * @param options - Options including `route`, `data`, `params`, and `formData`.
   * @returns A Promise resolving to the API response.
   */
  put({ route = [], data, params, formData }: any) {
    return this.request({
      method: PUT,
      route,
      data,
      params,
      formData
    });
  }

  /**
   * Performs a DELETE request — delegates to the base DataService class with the `DELETE` HTTP method.
   *
   * @param options - Options including `route`, `data`, and `params`.
   * @returns A Promise resolving to the API response.
   */
  delete({ route = [], data, params }: any) {
    return this.request({
      method: DELETE,
      route,
      data,
      params
    });
  }
}

export default DataService;
